import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useFormContext, useWatch } from "react-hook-form";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { isOptimizationInvalidationCancelled } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { deleteProcessStep } from "#modules/processSteps/infra/deleteProcessStep.api";
import { quoteRequestLineQuery } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { quoteRequestProcessStepsQuery } from "#modules/quoteRequests/infra/quoteRequestProcessSteps.query";
import { quoteRequestTransportQuery } from "#modules/quoteRequests/infra/quoteRequestTransport.query";
import { quoteRequestAtom } from "#shared/view/components/AppShell/Atoms";

export const useDeleteProcessStep = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { control } = useFormContext<QuoteRequestLineFormData>();

  const quoteRequest = useAtomValue(quoteRequestAtom);
  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });

  const { mutate, ...rest } = useMutation({
    mutationFn: deleteProcessStep,

    onSuccess: () => {
      void queryClient.invalidateQueries(quoteRequestLineQuery({ id: quoteRequestLineId }));

      if (quoteRequest?.id) {
        void queryClient.invalidateQueries(quoteRequestTransportQuery(quoteRequest.id));

        void queryClient.invalidateQueries(quoteRequestProcessStepsQuery(quoteRequest.id));
      }
      options?.onSuccess?.();
    },

    onError: (err: Error) => {
      if (isOptimizationInvalidationCancelled(err)) {
        return;
      }

      showErrorToast({ title: err.message });
      errorHandler(err);
    },
  });

  return { deleteProcessStep: mutate, ...rest };
};
